import React from 'react';
import { Box, Paper, Typography, Stack, Chip, IconButton } from '@mui/material';
import { TrendingUp, TrendingDown, ShowChart, Close, Edit } from '@mui/icons-material';
import { Position } from '../../types';
import { formatCurrency } from '../../utils/formatters';

interface MobilePositionCardProps {
  position: Position;
  onViewChart?: (symbol: string) => void;
  onClose?: (position: Position) => void;
  onEdit?: (position: Position) => void;
}

const MobilePositionCard: React.FC<MobilePositionCardProps> = ({
  position,
  onViewChart,
  onClose,
  onEdit,
}) => {
  const gainLoss = (position.currentPrice - position.entryPrice) * position.quantity;
  const gainLossPercent = ((position.currentPrice - position.entryPrice) / position.entryPrice) * 100;
  const isPositive = gainLoss >= 0;

  const distanceToStop = position.stopLoss
    ? ((position.currentPrice - position.stopLoss) / position.currentPrice) * 100
    : null;
  const distanceToTarget = position.targetPrice
    ? ((position.targetPrice - position.currentPrice) / position.currentPrice) * 100
    : null;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.5,
        mb: 1,
        borderRadius: 2,
        borderLeft: '4px solid',
        borderLeftColor: isPositive ? 'success.main' : 'error.main',
        overflow: 'hidden',
      }}
    >
      {/* Row 1: Symbol + P/L */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight="bold">
          {position.symbol}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {isPositive ? (
            <TrendingUp sx={{ fontSize: 14 }} color="success" />
          ) : (
            <TrendingDown sx={{ fontSize: 14 }} color="error" />
          )}
          <Typography
            variant="body2"
            fontWeight="bold"
            color={isPositive ? 'success.main' : 'error.main'}
          >
            {isPositive ? '+' : ''}{formatCurrency(gainLoss)}
          </Typography>
          <Typography
            variant="caption"
            fontSize="0.65rem"
            color={isPositive ? 'success.main' : 'error.main'}
          >
            ({isPositive ? '+' : ''}{gainLossPercent.toFixed(1)}%)
          </Typography>
        </Stack>
      </Box>

      {/* Row 2: Stats chips */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip
          label={`${position.quantity} @ ${formatCurrency(position.entryPrice)}`}
          size="small"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
        <Chip
          label={formatCurrency(position.currentPrice)}
          size="small"
          variant="filled"
          color="primary"
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
        <Chip
          label={`${position.holdingDays}d`}
          size="small"
          variant="outlined"
          color={position.holdingDays >= 28 ? 'warning' : 'default'}
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
      </Box>

      {/* Row 3: Stop/Target + Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
          {position.stopLoss ? (
            <Typography variant="caption" color="error.main" fontSize="0.65rem" noWrap>
              S: {formatCurrency(position.stopLoss)} ({distanceToStop !== null ? `${distanceToStop.toFixed(1)}%` : ''})
            </Typography>
          ) : (
            <Typography variant="caption" color="warning.main" fontSize="0.65rem">
              No stop
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary" fontSize="0.65rem">|</Typography>
          {position.targetPrice ? (
            <Typography variant="caption" color="success.main" fontSize="0.65rem" noWrap>
              T: {formatCurrency(position.targetPrice)} (+{distanceToTarget !== null ? `${distanceToTarget.toFixed(1)}%` : ''})
            </Typography>
          ) : (
            <Typography variant="caption" color="warning.main" fontSize="0.65rem">
              No target
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={0} sx={{ flexShrink: 0, ml: 0.5 }}>
          {onViewChart && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onViewChart(position.symbol); }}
              sx={{ p: 0.25 }}
            >
              <ShowChart sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          {onEdit && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onEdit(position); }}
              sx={{ p: 0.25 }}
            >
              <Edit sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          {onClose && (
            <IconButton
              size="small"
              color="error"
              onClick={(e) => { e.stopPropagation(); onClose(position); }}
              sx={{ p: 0.25 }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default MobilePositionCard;
